import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { ErrorBanner } from './ErrorBanner';
import { Field, Input } from './Input';

/**
 * Minimal modal: overlay, Escape/overlay-click to close, initial focus on the
 * first field (or the dialog itself).
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  width = 'max-w-lg',
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const first = ref.current?.querySelector<HTMLElement>('input, textarea, select');
    (first ?? ref.current)?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-[10vh] backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`w-full ${width} rise rounded-card border border-line bg-surface shadow-xl outline-none`}
      >
        <h2 id={titleId} className="border-b border-line px-5 py-3.5 text-sm font-semibold text-ink">
          {title}
        </h2>
        <div className="px-5 py-4 text-sm text-ink">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Confirmation dialog. With `requireText`, the confirm button stays disabled
 * until the user types that exact text (for destructive actions).
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = 'Confirm',
  danger = false,
  requireText,
  loading = false,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  requireText?: string;
  loading?: boolean;
  error?: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const ok = !requireText || typed === requireText;
  const close = () => {
    setTyped('');
    onCancel();
  };
  return (
    <Modal
      open={open}
      title={title}
      onClose={close}
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            disabled={!ok}
            loading={loading}
            onClick={() => {
              if (ok) onConfirm();
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (ok && !loading) onConfirm();
        }}
      >
        {children && <div className="text-ink-2">{children}</div>}
        {requireText && (
          <Field
            label={
              <>
                Type <code className="rounded bg-subtle px-1 font-mono text-ink">{requireText}</code> to confirm
              </>
            }
          >
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" aria-label="Confirmation text" />
          </Field>
        )}
        <ErrorBanner error={error} />
      </form>
    </Modal>
  );
}
